import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/FloatingWhatsApp";
import { WindowGrid } from "@/components/motion/Reveal";

// Highlights the words MOVE and CARE in brand red wherever they appear.
export function MoveCare({ text, className = "" }) {
    const parts = text.split(/(MOVE|CARE)/g);
    return (
        <span className={className}>
            {parts.map((p, i) =>
                p === "MOVE" || p === "CARE" ? (
                    <span key={i} className="text-brand-red">
                        {p}
                    </span>
                ) : (
                    <span key={i}>{p}</span>
                )
            )}
        </span>
    );
}

// Small uppercase eyebrow label with the window-grid motif.
export function SectionLabel({ children, light = false, className = "" }) {
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <WindowGrid size={16} />
            <span
                className={`text-xs font-bold uppercase tracking-[0.25em] ${
                    light ? "text-white/80" : "text-brand-red"
                }`}
            >
                {children}
            </span>
        </div>
    );
}

// Primary red CTA button. Renders <a> for external, <Link> for internal.
export function CTAButton({
    children,
    to,
    href,
    onClick,
    variant = "red",
    whatsapp = false,
    className = "",
    testid,
    ...rest
}) {
    const base =
        "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold tracking-wide transition-all duration-300";
    const styles = {
        red: "bg-brand-red text-white hover:shadow-brand-lg hover:-translate-y-0.5",
        blue: "bg-brand-blue text-white hover:bg-brand-deep hover:-translate-y-0.5",
        outline:
            "border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white",
        light: "bg-white text-brand-blue hover:-translate-y-0.5 hover:shadow-brand-lg",
    };
    const cls = `${base} ${styles[variant]} ${className}`;
    const inner = (
        <>
            {whatsapp && <WhatsAppIcon className="h-4 w-4" />}
            {children}
            {!whatsapp && (
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
        </>
    );

    if (href)
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={cls}
                data-testid={testid}
                {...rest}
            >
                {inner}
            </a>
        );
    if (to)
        return (
            <Link to={to} className={cls} data-testid={testid} {...rest}>
                {inner}
            </Link>
        );
    return (
        <button onClick={onClick} className={cls} data-testid={testid} {...rest}>
            {inner}
        </button>
    );
}

// Inner-page hero with blue background, grain and the "protecting hand" curve.
export function PageHero({ label, title, subtitle, image }) {
    return (
        <section className="relative overflow-hidden bg-brand-blue grain">
            <div className="absolute inset-0 opacity-20">
                {image && (
                    <img
                        src={image}
                        alt=""
                        className="h-full w-full object-cover mix-blend-overlay"
                    />
                )}
            </div>
            <div className="relative mx-auto max-w-6xl px-6 pb-24 pt-36 sm:pt-40">
                {label && <SectionLabel light>{label}</SectionLabel>}
                <h1 className="mt-5 max-w-4xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                    <MoveCare text={title} />
                </h1>
                {subtitle && (
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
                        {subtitle}
                    </p>
                )}
            </div>
            <div className="absolute bottom-0 left-0 h-16 w-1/2 rounded-tr-[80px] bg-white sm:w-1/3" />
        </section>
    );
}
